import { CircularProgress } from "@mui/material";
import { singleTypeListQuery } from "api/graphqlTypes";
import { graphqlPost } from "api/graphqlClient";
import { RestError } from "api/restClient.types";
import { Button } from "components/Button/Button";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { CustomObjectChildNode } from "components/ObjectTemplateCanvas/CustomObjectChildNode";
import { CustomObjectContainerNode } from "components/ObjectTemplateCanvas/CustomObjectContainerNode";
import { FloatingEdge } from "components/ObjectTemplateCanvas/FloatingEdges";
import Tooltip from "components/Tooltip/Tooltip";
import { FeConfigType } from "config/config";
import { PartitionByWidgetConfig } from "containers/Dashboard/types";
import customObjectConfig from "config/resources/custom_object";
import { Direction } from "containers/Resources/screens/ResourcesSingle/components/Visualization/visualization.service";
import { FormApi } from "final-form";
import { Object_Template } from "graphql/generated/graphql";
import { cloneDeep } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import ReactFlow, {
    ControlButton,
    Controls,
    Node,
    ReactFlowInstance,
    ReactFlowProvider,
    useEdgesState,
    useNodesState,
} from "reactflow";
import "reactflow/dist/style.css";
import { actions as dataStoreAction } from "utils/DataStore/DataStore.reducer";
import { getSearchObj } from "utils/utils";
import customObjectStyles from "./CustomObjectNode.module.scss";
import styles from "./ObjectTemplateCard.module.scss";
import {
    CANVAS_CHILD_CARD_HEIGHT,
    customNodeEdge,
    customObjectChildNodeType,
    customObjectContainerNodeType,
    getPositionedCustomObjectGraphList,
    positionChildrenNodes,
} from "./utils";
import {
    ScopeComparison,
    isPrefilteredScope,
    locationScopeFilter,
    locationScopeFilterObject,
    resolveGraphFilter,
    serializeGraphFilter,
} from "./relationGraphFilter";
import {
    CUSTOM_OBJECTS_PER_BATCH,
    INITIAL_CUSTOM_OBJECTS_PER_TEMPLATE,
    NEIGHBORHOOD_CAP,
    TEMPLATES_WITH_OBJECTS_PER_BATCH,
    fetchContainerCustomObjectGraph,
    fetchContainerTemplateCustomObjects,
    fetchCustomObjectGraphTemplates,
    fetchCustomObjectNeighborhood,
    fetchCustomObjectTotalCount,
} from "./relationGraphFetch";
import embedStyles from "./RelationGraphEmbed.module.scss";

export interface NestedFormsDataProps {
    [key: string]: FormApi;
}

/** Sum of custom objects currently loaded across all template containers. */
const countLoadedObjects = (list: Object_Template[]): number =>
    list.reduce(
        (sum, template) =>
            sum +
            (template.custom_object_list_object_template_id?.custom_object_list?.length ?? 0),
        0
    );

/** The focus custom object within a merged template list, or null. */
const findFocusObject = (list: Object_Template[], focusId?: string): any => {
    if (!focusId) {
        return null;
    }
    for (const template of list) {
        const found = (
            template?.custom_object_list_object_template_id?.custom_object_list ?? []
        ).find((object) => object?.id === focusId);
        if (found) {
            return found;
        }
    }
    return null;
};

/**
 * Ensure the started-from focus object is present in its template container. The
 * eager "Load more" (full-canvas) path loads only the first name-ordered page of
 * objects per template, so a focus object further down the alphabet would be
 * missing — leaving the highlight / re-center with no node to target (Margot,
 * 2026-09-08: "the one I started with is simply not shown at all anymore … a
 * subset of 10 objects is loaded per template"). Inject the focus object (captured
 * from the neighborhood seed) at the front of its template's list if absent.
 */
const ensureFocusObjectPresent = (
    list: Object_Template[],
    focusId: string | undefined,
    focusObject: any
): Object_Template[] => {
    if (!focusId || !focusObject) {
        return list;
    }
    const templateId = focusObject.object_template_id;
    if (!templateId) {
        return list;
    }
    let handled = false;
    const next = list.map((template) => {
        if (template.id !== templateId) {
            return template;
        }
        handled = true;
        const objects =
            template.custom_object_list_object_template_id?.custom_object_list ?? [];
        if (objects.some((object) => object?.id === focusId)) {
            return template;
        }
        const total =
            template.custom_object_list_object_template_id?.custom_object_count ??
            objects.length;
        return {
            ...template,
            custom_object_list_object_template_id: {
                ...template.custom_object_list_object_template_id,
                custom_object_count: Math.max(total, objects.length + 1),
                custom_object_list: [focusObject, ...objects],
            },
        };
    });
    if (!handled) {
        // The focus object's template was not in the eager list at all (the eager
        // path loads every template, so this is a safety net): add a container for it.
        next.push({
            id: templateId,
            name: focusObject.name ?? templateId,
            custom_object_list_object_template_id: {
                custom_object_count: 1,
                custom_object_list: [focusObject],
            },
        } as Object_Template);
    }
    return next;
};

interface ObjectTemplateCanvasScreenProps {
    setDirtyScreen?: (value: boolean) => void;
    setNewObjectTemplateShown?: (value: boolean) => void;
    isNewObjectTemplateShown?: boolean;
    hasChanges?: boolean;
    location?: FeConfigType;
    locationId?: string;
    /** Optional GraphQL filter on object_template_list (or custom_object_list via customObjectFilter). */
    filter?: unknown;
    /** When set with embedMode/custom object graphs, seeds from custom_object_list first. */
    customObjectFilter?: unknown;
    /** Latest N custom objects per distinct field value (partition_by). */
    partitionBy?: PartitionByWidgetConfig;
    customObjectGroupLimit?: number;
    customObjectOrder?: string;
    embedMode?: boolean;
    className?: string;
    /**
     * Deep-link focus target: id of the custom object the canvas was opened from.
     * The matching node is highlighted and the view centers on it once it loads.
     */
    focusId?: string;
}

/**
 * CustomObjectCanvas component that renders a ReactFlow-based visualization
 * of custom objects and their relationships.
 *
 * This component handles:
 * - Fetching and displaying custom object data
 * - Dynamic node positioning and layout
 * - Height calculations for expanded/collapsed nodes
 * - Interactive edge highlighting
 * - Layout direction switching (vertical/horizontal)
 *
 * @param location - The type of location (project or bundle)
 * @param locationId - The unique identifier for the location
 * @returns JSX.Element - The rendered custom object canvas
 */
const CustomObjectCanvas = ({
    location,
    locationId,
    filter,
    customObjectFilter,
    partitionBy,
    customObjectGroupLimit,
    customObjectOrder,
    embedMode = false,
    className,
    focusId,
}: ObjectTemplateCanvasScreenProps) => {
    const dispatch = useDispatch();
    const key = `objectTemplateListWithCustomObjects`;
    const resolvedTemplateFilter = resolveGraphFilter(filter, location, locationId);
    const resolvedCustomObjectFilter =
        serializeGraphFilter(customObjectFilter) ?? resolvedTemplateFilter;
    // Custom-object-scoped filter for the precise "of M" partial-view total. The
    // canvas is always scoped to its container, so M must reflect the SAME
    // project/bundle scope — not the whole workspace (Margot, 2026-09-01). Prefer an
    // explicit custom-object filter; otherwise derive the container scope
    // (`project_id`/`bundle_id eq locationId`) via locationScopeFilter — the
    // custom_object resource carries those columns, so the filter is valid. A
    // workspace-scoped canvas (or an unresolved locationId) yields null → the
    // whole-workspace count, which is the correct total for that scope.
    const totalCountFilter = useMemo(
        () =>
            serializeGraphFilter(customObjectFilter) ??
            locationScopeFilter(location, locationId),
        [customObjectFilter, location, locationId]
    );
    const preFiltered =
        isPrefilteredScope(filter) ||
        isPrefilteredScope(customObjectFilter) ||
        !!partitionBy?.field ||
        // A ?focus deep-link seeds a bounded neighborhood whose templates may sit
        // outside the current location — treat it as pre-filtered so those
        // neighbor containers still render.
        !!focusId ||
        embedMode;

    // A plain project/bundle canvas (no explicit filter / partition / focus /
    // embed) is scoped by the custom objects that LIVE in the container, not by
    // the templates defined there — so a container holding objects of templates
    // defined elsewhere (e.g. Agent_ops_content) still renders them instead of an
    // empty "no objects" canvas. Null for the workspace canvas and any
    // pre-filtered mode, which keep the template-enumeration / seed paths.
    // (operator-task-fe-canvas-empty-when-container-has-objects-no-templates)
    const containerScope = useMemo<ScopeComparison | null>(
        () =>
            !embedMode &&
            !focusId &&
            !isPrefilteredScope(filter) &&
            (location === "project" || location === "bundle")
                ? locationScopeFilterObject(location, locationId)
                : null,
        [embedMode, focusId, filter, location, locationId]
    );

    // Whether the canvas SCOPE is settled enough to fetch. A project/bundle canvas
    // opened by ?name= knows its `location` before `locationId` resolves (Canvas.tsx
    // looks the id up from the projects/bundles store, which hydrates async). Fetching
    // during that window pulls EVERY template AND its nested custom objects across the
    // whole workspace — the heaviest query on the object canvas — only to be superseded
    // by the correctly-scoped fetch once the id resolves. Gate the fetch on a settled
    // scope (operator-task-fe-canvas-loading-perf-followup). Any pre-filtered mode
    // (embed / dashboard widget / explicit filter / ?focus neighborhood) and an
    // intentional workspace-global canvas are settled immediately.
    const scopeReady =
        preFiltered ||
        location === "workspace" ||
        ((location === "project" || location === "bundle") && !!locationId);
    const seedPartitionBy = useMemo(() => {
        if (!partitionBy?.field) {
            return undefined;
        }
        return {
            field: partitionBy.field,
            limit: partitionBy.limit ?? 1,
            order: partitionBy.order ?? customObjectOrder ?? "-created_at",
        };
    }, [partitionBy, customObjectOrder]);

    const [layoutDirection, setLayoutDirection] = useState<Direction>(Direction.Vertical);

    // ReactFlow state management for nodes and edges
    const [nodes, setNodes] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    // ReactFlow instance reference for view manipulation
    const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);

    const [isLoading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [childrenHeightData, setChildrenHeightData] = useState({});
    const [expandedCustomObjectId, setExpandedCustomObjectId] = useState<string | null>(null);

    // Partial-view transparency for the deep-link (?focus) neighborhood: signal that
    // the canvas shows only a bounded slice, and offer controls to reveal more /
    // load the whole canvas.
    // (operator-task-fe-canvas-partial-load-hidden-objects-transparency)
    const [isPartialView, setIsPartialView] = useState(false);
    const [loadedCount, setLoadedCount] = useState(0);
    // Precise workspace-scope total for "showing N of M"; null while it loads lazily.
    const [totalCount, setTotalCount] = useState<number | null>(null);
    const [confirmingLoadAll, setConfirmingLoadAll] = useState(false);

    // This canvas eagerly loads nested custom objects per template, so the
    // per-query GraphQL cost is (bodyCost + 1) * TEMPLATES_PER_BATCH. A batch of
    // 20 pushed the cost to 3800 (> the 2000 GRAPHQL_QUERY_COMPLEXITY_LIMIT) and
    // broke the canvas on any project/bundle with enough templates/objects.
    // Reuse the smaller "templates with nested objects" batch (as the embed path
    // already does); the remainder is loaded progressively by the paging loops
    // below (operator-task-graphql-canvas-query-cost-limit-exceeded).
    const TEMPLATES_PER_BATCH = TEMPLATES_WITH_OBJECTS_PER_BATCH;

    const [, setItems] = useState<any[]>([]);
    const fetchCustomObjectListRef = useRef<
        (offset: number, generation?: number, forceFull?: boolean) => Promise<unknown>
    >(() => Promise.resolve());
    // Generation token for the recursive progressive-fetch loop. It is bumped
    // whenever a new top-level fetch starts (offset 0) or the component unmounts.
    // Each loop invocation captures the generation it belongs to and stops paging
    // / writing state once it no longer matches — so an in-flight paging loop does
    // not keep fetching, re-laying-out and calling setState after the canvas is
    // navigated away from (fan-whirr), nor race a concurrently-started loop
    // (operator-task-fe-canvas-loading-perf-followup, item #4).
    const fetchGenerationRef = useRef(0);
    useEffect(
        () => () => {
            // Invalidate any in-flight paging loop on unmount.
            fetchGenerationRef.current += 1;
        },
        []
    );
    // Authoritative merged template list (with each template's currently-loaded
    // custom objects). Held in a ref so the per-container "load more" handler can
    // append the next page and rebuild the graph without re-fetching everything.
    const mergedListRef = useRef<Object_Template[]>([]);
    const loadMoreRef = useRef<(templateId: string, loadAll?: boolean) => void>(
        () => undefined
    );
    // Latest container scope (null off the plain project/bundle path), read by
    // the deps-stable fetchObjects so per-container "load more" pages stay inside
    // the container. (operator-task-fe-canvas-empty-when-container-has-objects-no-templates)
    const containerScopeRef = useRef<ScopeComparison | null>(null);
    containerScopeRef.current = containerScope;
    // Last-computed container (object_template) positions, kept so an interactive
    // "load more" can pin already-placed containers and avoid a whole-graph
    // re-layout jump when one container reveals more objects.
    const containerPositionsRef = useRef<{ [id: string]: { x: number; y: number } }>({});
    // Set by Load more / Load all to request a one-shot re-center on the focus node
    // once the rebuilt graph paints (Margot (e)).
    const recenterFocusRef = useRef(false);
    // The started-from focus custom object, captured from the neighborhood seed so
    // the eager "Load more" (full-canvas) path can re-inject it if it falls outside
    // the first name-ordered page loaded per template (Margot, 2026-09-08).
    const focusObjectRef = useRef<any>(null);
    // Per-template NAME-paging cursor for the per-container "load more" / "load all".
    // It counts rows already PAGED by name (+name order), which — in the partial/focus
    // view — is deliberately DECOUPLED from the number of objects currently displayed:
    // the neighborhood seed loads a scattered (non-prefix) subset, so paging must
    // start from 0 and merge by id, not resume at the displayed length (which would
    // skip the first rows by name and could never reach the true total). On the normal
    // canvas the cursor is unset and defaults to the displayed length — preserving the
    // existing prefix-paging behavior exactly.
    // (operator-task-fe-canvas-partial-load-hidden-objects-transparency: Margot (a)+(d))
    const templatePagedRef = useRef<{ [templateId: string]: number }>({});
    // Guard against overlapping per-container loads on the same template (the load
    // no longer toggles the whole-canvas overlay, so a double-click could otherwise
    // fire two concurrent fetches and double-advance the paging cursor).
    const loadingTemplatesRef = useRef<Set<string>>(new Set());

    const buildGraph = useCallback(
        (
            mergedList: Object_Template[],
            pinnedContainerPositions?: { [id: string]: { x: number; y: number } }
        ) => {
            const [nodes, edges] = getPositionedCustomObjectGraphList({
                objectTemplateList: mergedList,
                location,
                locationId,
                preFiltered,
                layoutDirection,
                pinnedContainerPositions,
                additionalData: {
                    fetchCustomObjectList: () => fetchCustomObjectListRef.current(0),
                    setLoading,
                    formStructure: customObjectConfig.form,
                    embedMode,
                    readOnly: embedMode,
                    loadMoreForTemplate: (templateId: string, loadAll?: boolean) =>
                        loadMoreRef.current(templateId, !!loadAll),
                },
            });
            // Remember where each container landed so the next "load more" can pin
            // them (container nodes have no parentId; children are parent-relative).
            const nextContainerPositions: { [id: string]: { x: number; y: number } } = {};
            nodes.forEach((node: any) => {
                if (!node.parentId && node.position) {
                    nextContainerPositions[node.id] = { ...node.position };
                }
            });
            containerPositionsRef.current = nextContainerPositions;
            setNodes((_) =>
                nodes.map((node) => ({
                    ...node,
                    data: {
                        ...node.data,
                        childrenHeightData,
                        setExpandedCustomObjectId,
                    },
                }))
            );
            setEdges(edges as any);
            setExpandedCustomObjectId(null);
        },
        [
            location,
            locationId,
            preFiltered,
            layoutDirection,
            childrenHeightData,
            embedMode,
            setNodes,
            setEdges,
        ]
    );

    const fetchEmbeddedGraph = useCallback(async () => {
        // Start a new generation so an in-flight embed load that is superseded (or
        // whose canvas unmounts — the unmount effect bumps the ref) stops paging
        // inside relationGraphFetch and does not build the graph / write state after
        // the fact (operator-task-fe-canvas-embed-path-abort-on-unmount).
        const gen = (fetchGenerationRef.current += 1);
        setLoading(true);
        setFetchError(null);
        try {
            const mergedList = await fetchCustomObjectGraphTemplates(
                resolvedTemplateFilter,
                resolvedCustomObjectFilter,
                {
                    partitionBy: seedPartitionBy,
                    groupLimit: customObjectGroupLimit,
                    order: customObjectOrder ?? "-created_at",
                    shouldContinue: () => gen === fetchGenerationRef.current,
                }
            );
            if (gen !== fetchGenerationRef.current) {
                return;
            }
            buildGraph(mergedList);
        } catch (err: any) {
            if (gen !== fetchGenerationRef.current) {
                return;
            }
            setFetchError(
                err?.response?.data?.message ?? err?.message ?? "Failed to load relation graph."
            );
            setNodes([]);
            setEdges([]);
        } finally {
            if (gen === fetchGenerationRef.current) {
                setLoading(false);
            }
        }
    }, [
        resolvedTemplateFilter,
        resolvedCustomObjectFilter,
        seedPartitionBy,
        customObjectGroupLimit,
        customObjectOrder,
        buildGraph,
        setNodes,
        setEdges,
    ]);

    const fetchObjects = useCallback(async (idList: string[], offset: number) => {
        try {
            // Container canvas: page a single template's objects WITHIN the
            // container (per-container "load more" fires one template at a time),
            // so a foreign template's objects from other containers never leak in.
            // (operator-task-fe-canvas-empty-when-container-has-objects-no-templates)
            const containerScopeFilter = containerScopeRef.current;
            if (containerScopeFilter && idList.length === 1) {
                return await fetchContainerTemplateCustomObjects(
                    containerScopeFilter,
                    idList[0],
                    offset,
                    CUSTOM_OBJECTS_PER_BATCH
                );
            }
            const params = {
                query: `query object_template_list($filter: String, $limit: Int, $order: String, $offset: Int,$customObjectLimit: Int, $customObjectOffset: Int) {
                            object_template_list (filter: $filter, limit: $limit, order: $order, offset: $offset) {
                                object_template_count
                                object_template_list {
                                    id
                                    custom_object_list_object_template_id(limit: $customObjectLimit, offset: $customObjectOffset, order: $order){
                                        custom_object_count
                                        custom_object_list {
                                            id
                                            name
                                            object_template_id
                                            provisioning_state
                                            value
                                            is_readonly
                                            deleted_at
                                            project_id
                                            bundle_id
                                            referencing_custom_objects
                                            project_id_project{
                                                id
                                                name
                                            }
                                            bundle_id_bundle{
                                                id
                                                name
                                            }
                                        }
                                    }
                                }
                            
                }    }`,
                variables: {
                    filter: JSON.stringify(getSearchObj({ field: "id", op: "in", value: idList })),
                    limit: idList.length,
                    offset: 0,
                    customObjectLimit: CUSTOM_OBJECTS_PER_BATCH,
                    customObjectOffset: offset,
                    order: "+name",
                },
                allow_normal: true,
                allow_deleted: true,
            };

            const result = (await graphqlPost(params)) as any;
            const objectTemplateList =
                result?.data?.object_template_list?.object_template_list ?? [];

            return objectTemplateList;
        } catch (error) {
            NotificationService.error(
                <RestErrorNotification
                    error={error as RestError}
                    titleOverride="Cannot fetch custom objects"
                />
            );
        }
    }, []);

    const fetchCustomObjectList = useCallback(
        (offset: number, generation?: number, forceFull?: boolean) => {
            // A top-level fetch (offset 0) starts a new generation; a recursive
            // page-fetch inherits the generation of the loop it belongs to. A
            // stale loop (canvas unmounted, or superseded by a newer fetch) stops
            // paging and writing state once its generation no longer matches.
            const gen = generation ?? (fetchGenerationRef.current += 1);
            // Deep-link neighborhood seed: when opened from a specific record
            // (?focus=<id>), seed the initial view with just that record + its
            // immediate neighbors instead of paging the whole container scope.
            // (operator-task-fe-canvas-highlight-record-and-neighbors-on-link)
            if (focusId && !embedMode && offset === 0 && !forceFull) {
                setLoading(true);
                setFetchError(null);
                setConfirmingLoadAll(false);
                return fetchCustomObjectNeighborhood(focusId, NEIGHBORHOOD_CAP)
                    .then((mergedList) => {
                        if (gen !== fetchGenerationRef.current) {
                            return;
                        }
                        mergedListRef.current = mergedList;
                        setItems(mergedList);
                        // Capture the started-from object so the eager "Load more"
                        // (full-canvas) path can guarantee it stays present and
                        // highlighted (Margot, 2026-09-08).
                        focusObjectRef.current =
                            findFocusObject(mergedList, focusId) ?? focusObjectRef.current;
                        // Partial-view templates hold a non-prefix neighborhood subset,
                        // so per-container "load more" must page by name from 0 and
                        // merge by id (see templatePagedRef).
                        templatePagedRef.current = {};
                        mergedList.forEach((template) => {
                            if (template?.id) {
                                templatePagedRef.current[template.id] = 0;
                            }
                        });
                        buildGraph(mergedList);
                        // Surface that this is a bounded partial view + a precise
                        // "showing N of M" (M fetched lazily, count-only).
                        setLoadedCount(countLoadedObjects(mergedList));
                        setIsPartialView(true);
                        // M is fetched by a dedicated effect keyed on the scope
                        // filter, so it re-resolves if the container scope settles
                        // after this seed (e.g. locationId hydrates late). Reset to
                        // null here to show the lazy spinner until it lands.
                        setTotalCount(null);
                    })
                    .catch((err: any) => {
                        if (gen !== fetchGenerationRef.current) {
                            return;
                        }
                        setFetchError(
                            err?.response?.data?.message ??
                                err?.message ??
                                "Failed to load relation graph."
                        );
                        setNodes([]);
                        setEdges([]);
                    })
                    .finally(() => {
                        if (gen !== fetchGenerationRef.current) {
                            return;
                        }
                        setLoading(false);
                    });
            }
            if (embedMode) {
                return fetchEmbeddedGraph();
            }
            // Container canvas (plain project/bundle scope): seed by the custom
            // objects that LIVE in the container, grouped by their real templates
            // (which may be defined in other containers), instead of enumerating
            // templates defined in the container. Fixes the empty "no objects"
            // canvas for a container with objects but no local templates.
            // (operator-task-fe-canvas-empty-when-container-has-objects-no-templates)
            if (containerScope && offset === 0) {
                setLoading(true);
                setFetchError(null);
                return fetchContainerCustomObjectGraph(
                    containerScope,
                    INITIAL_CUSTOM_OBJECTS_PER_TEMPLATE,
                    { shouldContinue: () => gen === fetchGenerationRef.current }
                )
                    .then((mergedList) => {
                        if (gen !== fetchGenerationRef.current) {
                            return;
                        }
                        mergedListRef.current = mergedList;
                        setItems(mergedList);
                        // Reset per-template name cursors: the container seed loads
                        // the first K objects by name per template (prefix), so
                        // "load more" resumes from the displayed length (the unset
                        // default), paging +name within the container.
                        templatePagedRef.current = {};
                        buildGraph(mergedList);
                    })
                    .catch((err: any) => {
                        if (gen !== fetchGenerationRef.current) {
                            return;
                        }
                        setFetchError(
                            err?.response?.data?.message ??
                                err?.message ??
                                "Failed to load relation graph."
                        );
                        setNodes([]);
                        setEdges([]);
                    })
                    .finally(() => {
                        if (gen !== fetchGenerationRef.current) {
                            return;
                        }
                        setLoading(false);
                    });
            }
            setLoading(true);
            return new Promise(() => {
                dispatch({
                    type: dataStoreAction.START_FETCH_DATA,
                    key,
                    params: {
                        query: singleTypeListQuery("object_template"),
                        variables: {
                            limit: TEMPLATES_PER_BATCH,
                            order: "+name",
                            offset,
                            filter: resolvedTemplateFilter,
                            attributeLimit: 0,
                            customObjectLimit: INITIAL_CUSTOM_OBJECTS_PER_TEMPLATE,
                            referenceLimit: 0,
                        },
                        allow_normal: true,
                        allow_deleted: true,
                    },
                    promise: {
                        resolve: async (response: any) => {
                            // Stale loop — the canvas unmounted or a newer fetch
                            // superseded this one: stop paging and writing state.
                            if (gen !== fetchGenerationRef.current) {
                                return;
                            }
                            const newItems =
                                response?.object_template_list?.object_template_list ?? [];
                            const count =
                                response?.object_template_list?.object_template_count ?? 0;

                            // Bounded initial paint: the query above already returns
                            // the first INITIAL_CUSTOM_OBJECTS_PER_TEMPLATE custom
                            // objects per template plus each template's total count.
                            // Do NOT exhaustively page every custom object here — the
                            // remainder loads on demand via the per-container
                            // "load more" affordance, keeping initial paint bounded by
                            // (#templates x K) rather than the total object count.
                            const newItemsWithObjects = newItems;

                            const newOffset = offset + newItems.length;
                            let mergedListForGraph: Object_Template[] | null = null;

                            setItems((currentItems) => {
                                let mergedList;

                                if (offset === 0) {
                                    mergedList = newItemsWithObjects;
                                } else {
                                    mergedList = [...currentItems, ...newItemsWithObjects];
                                }

                                mergedListRef.current = mergedList;

                                if (newOffset < count) {
                                    return mergedList;
                                }

                                mergedListForGraph = mergedList;
                                return mergedList;
                            });

                            if (mergedListForGraph) {
                                // Guarantee the started-from focus object is present
                                // even if it fell outside the first name-ordered page
                                // loaded for its template, so the highlight / re-center
                                // has a node to target (Margot, 2026-09-08).
                                const finalList = ensureFocusObjectPresent(
                                    mergedListForGraph,
                                    focusId,
                                    focusObjectRef.current
                                );
                                mergedListRef.current = finalList;
                                buildGraph(finalList);
                            }
                            if (newOffset < count) {
                                fetchCustomObjectList(newOffset, gen);
                            } else {
                                setLoading(false);
                            }
                        },
                        reject: () => {
                            if (gen !== fetchGenerationRef.current) {
                                return;
                            }
                            setLoading(false);
                        },
                    },
                });
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            embedMode,
            focusId,
            containerScope,
            fetchEmbeddedGraph,
            dispatch,
            customObjectConfig.form,
            key,
            layoutDirection,
            location,
            locationId,
            resolvedTemplateFilter,
            buildGraph,
            setEdges,
            setNodes,
        ]
    );

    fetchCustomObjectListRef.current = fetchCustomObjectList;

    // Per-container "load more" / "load all": fetch the next page (or all
    // remaining) custom objects for a single template, merge them into the retained
    // list, and rebuild the graph — without re-fetching the whole scope.
    //
    // Paging is driven by a per-template NAME cursor (templatePagedRef), NOT by the
    // number of objects currently displayed. This matters in the partial/focus view,
    // where the displayed objects are a scattered (non-prefix) neighborhood subset:
    // resuming at the displayed length would skip the first rows by name and — worse
    // — a "load all" that stopped at `displayed >= total` could loop forever fetching
    // empty pages when the skipped rows were never loaded. Instead we page +name from
    // the cursor and MERGE BY ID, so overlaps are absorbed and the loop terminates on
    // a short (end-of-list) page. On the normal canvas the cursor is unset and
    // defaults to the displayed length, preserving the existing prefix-paging exactly.
    // The load no longer toggles the whole-canvas loading overlay, so the graph stays
    // on screen while a single container grows (Margot: don't blank the canvas).
    // (operator-task-fe-canvas-partial-load-hidden-objects-transparency: Margot (a)+(d))
    const loadMoreForTemplate = useCallback(
        async (templateId: string, loadAll = false) => {
            if (loadingTemplatesRef.current.has(templateId)) {
                return;
            }
            const currentList = mergedListRef.current;
            const template = currentList.find((item) => item.id === templateId);
            if (!template) {
                return;
            }
            const existingObjects =
                template.custom_object_list_object_template_id?.custom_object_list ?? [];
            const total =
                template.custom_object_list_object_template_id?.custom_object_count ?? 0;
            if (existingObjects.length >= total) {
                return;
            }
            loadingTemplatesRef.current.add(templateId);
            try {
                const objectById = new Map<string, any>();
                existingObjects.forEach((object) => {
                    if (object?.id) {
                        objectById.set(object.id, object);
                    }
                });
                let cursor = templatePagedRef.current[templateId] ?? existingObjects.length;

                // One page per click; "load all" keeps paging until an end-of-list
                // (short) page or the true total is covered.
                do {
                    const fetched: Object_Template[] = await fetchObjects([templateId], cursor);
                    const newObjects =
                        fetched?.find((item) => item.id === templateId)
                            ?.custom_object_list_object_template_id?.custom_object_list ?? [];
                    newObjects.forEach((object) => {
                        if (object?.id) {
                            objectById.set(object.id, object);
                        }
                    });
                    cursor += newObjects.length;
                    // End of list: a short page means there is nothing more to page.
                    if (newObjects.length < CUSTOM_OBJECTS_PER_BATCH) {
                        break;
                    }
                } while (loadAll && objectById.size < total);

                templatePagedRef.current[templateId] = cursor;
                const mergedObjects = Array.from(objectById.values());
                const updatedTemplate: Object_Template = {
                    ...template,
                    custom_object_list_object_template_id: {
                        ...template.custom_object_list_object_template_id,
                        // Never let the display count exceed the true total, but if we
                        // discovered MORE than the cached total (stale count), trust
                        // what we actually loaded.
                        custom_object_count: Math.max(total, mergedObjects.length),
                        custom_object_list: mergedObjects,
                    },
                };
                const updatedList = currentList.map((item) =>
                    item.id === templateId ? updatedTemplate : item
                );
                mergedListRef.current = updatedList;
                setItems(updatedList);
                // Keep the partial-view banner's "showing N of M" honest as per-container
                // loads reveal more (no-op on the normal canvas, where the banner is hidden).
                setLoadedCount(countLoadedObjects(updatedList));
                // Pin the containers already on screen so only the expanded
                // container grows — the rest of the graph stays put.
                buildGraph(updatedList, containerPositionsRef.current);
            } catch (error) {
                NotificationService.error(
                    <RestErrorNotification
                        error={error as RestError}
                        titleOverride="Cannot load more custom objects"
                    />
                );
            } finally {
                loadingTemplatesRef.current.delete(templateId);
            }
        },
        [fetchObjects, buildGraph]
    );

    loadMoreRef.current = loadMoreForTemplate;

    // "Load more…": drop the neighborhood bound and load the full canvas (the eager
    // path the partial load exists to avoid) — guarded by an inline confirm.
    const loadAllCanvas = useCallback(() => {
        setConfirmingLoadAll(false);
        setIsPartialView(false);
        // Re-center on the started-from object once the full graph paints, so it is
        // not lost among the newly-loaded nodes (Margot (e)). The focus node arrives
        // in a later progressive batch; the re-center effect fires when it appears.
        recenterFocusRef.current = true;
        fetchCustomObjectListRef.current(0, undefined, true);
    }, []);

    // Fetch data when dependencies change
    useEffect(() => {
        // Do not fetch until the scope is settled — otherwise a ?name= deep-link
        // fires an unfiltered whole-workspace fetch (templates + nested objects)
        // before its id resolves (see scopeReady above). Once locationId resolves
        // this effect re-runs and fires the single correctly-scoped fetch.
        if (!scopeReady) {
            return;
        }
        if (embedMode || customObjectConfig.form?.length || focusId) {
            fetchCustomObjectList(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        layoutDirection,
        customObjectConfig.form,
        location,
        locationId,
        resolvedTemplateFilter,
        resolvedCustomObjectFilter,
        seedPartitionBy,
        customObjectGroupLimit,
        customObjectOrder,
        embedMode,
        focusId,
        scopeReady,
    ]);

    // Define custom node types for ReactFlow
    const nodeTypes = useMemo(
        () => ({
            [customObjectChildNodeType]: CustomObjectChildNode,
            [customObjectContainerNodeType]: CustomObjectContainerNode,
        }),
        []
    );

    // Define custom edge types for ReactFlow
    const edgeTypes = useMemo(
        () => ({
            [customNodeEdge]: FloatingEdge,
        }),
        []
    );

    // Decorate the deep-link focus node with a highlight class (no-op otherwise).
    const displayNodes = useMemo(() => {
        if (!focusId) {
            return nodes;
        }
        return nodes.map((node) =>
            node.id === focusId
                ? {
                      ...node,
                      className: `${node.className ?? ""} ${
                          customObjectStyles.focusedNode
                      }`.trim(),
                      data: { ...node.data, isFocused: true },
                  }
                : node
        );
    }, [nodes, focusId]);

    // Update nodes with latest expansion state and height data
    useEffect(() => {
        setNodes((nds) => {
            const newNodes = [...nds];
            return newNodes.map((node) => ({
                ...node,
                data: {
                    ...cloneDeep(node.data),
                    expandedCustomObjectId,
                    setExpandedCustomObjectId,
                    childrenHeightData,
                },
            }));
        });
    }, [childrenHeightData, expandedCustomObjectId, setNodes]);

    // Fit view when ReactFlow instance is available. When a focus target is set
    // (deep-link from a record), the focus effect below owns the view instead.
    useEffect(() => {
        if (focusId) {
            return;
        }
        reactFlowInstance?.fitView();
    }, [reactFlowInstance, focusId]);

    // Re-fit the view once container heights have been measured. Container height
    // is measured from the DOM asynchronously after first paint (see the effect
    // below), so the initial fitView runs against pre-measurement sizes and the
    // view is left off-centre / zoomed onto empty space. Re-fitting after the
    // measurement settles frames the actually-loaded objects. The focus effect
    // owns the view when a deep-link focus target is set; the embed path is
    // read-only and keeps its own framing.
    useEffect(() => {
        if (focusId || embedMode || !reactFlowInstance) {
            return;
        }
        if (Object.keys(childrenHeightData).length === 0) {
            return;
        }
        const timeoutId = setTimeout(() => {
            reactFlowInstance.fitView({ maxZoom: 1.5, minZoom: 0.2, duration: 200 });
        }, 200);
        return () => clearTimeout(timeoutId);
    }, [reactFlowInstance, focusId, embedMode, childrenHeightData]);

    // Deep-link focus: once the focused custom-object node has loaded (it may
    // arrive in a later progressive batch), center the view on it — one-shot.
    const hasFocusedRef = useRef(false);
    useEffect(() => {
        if (!reactFlowInstance || !focusId || hasFocusedRef.current) {
            return;
        }
        const focusNode = nodes.find((node) => node.id === focusId);
        if (focusNode) {
            // Frame the focused object's CONTAINER (its parent template) when it has
            // one, so the whole sibling list is visible with the highlighted node
            // inside it — not zoomed tight onto the single card (Margot: "the list
            // isn't shown at all / zoom is very high"). Fall back to the node itself.
            const parentId = (focusNode as { parentId?: string }).parentId;
            reactFlowInstance.fitView({
                nodes: parentId ? [{ id: parentId }] : [{ id: focusId }],
                maxZoom: 1.0,
                minZoom: 0.2,
                duration: 400,
            });
            hasFocusedRef.current = true;
        }
    }, [reactFlowInstance, focusId, nodes]);

    // Precise "of M" total for the partial-view banner. Fetched here (not inline in
    // the seed fetch) so it re-resolves whenever the container SCOPE changes — e.g.
    // a ?name= deep-link whose locationId hydrates after the focus seed already
    // painted — instead of being pinned to a stale (often whole-workspace) count.
    // (operator-task-fe-canvas-partial-load-hidden-objects-transparency: Margot (c))
    useEffect(() => {
        if (!focusId || embedMode || !isPartialView) {
            return;
        }
        let cancelled = false;
        setTotalCount(null);
        fetchCustomObjectTotalCount(totalCountFilter)
            .then((total) => {
                if (!cancelled) {
                    setTotalCount(total);
                }
            })
            .catch(() => {
                /* best-effort: banner falls back to "showing N". */
            });
        return () => {
            cancelled = true;
        };
    }, [focusId, embedMode, isPartialView, totalCountFilter]);

    // Re-center on the focused object after Load more / Load all. The highlight
    // itself is preserved by id (displayNodes), but the viewport is not re-framed,
    // so the started-from object gets lost among the newly-revealed nodes (Margot
    // (e): "when loading all, the focus is gone … I don't even know if it is
    // shown"). When a load-more/all sets this flag, frame the focus NODE itself
    // (centered) once it is present in the rebuilt graph — one-shot per request.
    // The focus node may arrive in a later progressive batch, so this effect keys
    // on `nodes` and fires when it appears.
    useEffect(() => {
        if (!reactFlowInstance || !focusId || !recenterFocusRef.current) {
            return;
        }
        const focusNode = nodes.find((node) => node.id === focusId);
        if (focusNode) {
            reactFlowInstance.fitView({
                nodes: [{ id: focusId }],
                maxZoom: 1.0,
                minZoom: 0.2,
                duration: 400,
            });
            recenterFocusRef.current = false;
        }
    }, [reactFlowInstance, focusId, nodes]);

    // Re-fit the viewport after a layout-direction flip (item 7,
    // operator-task-fe-custom-object-canvas-display-improvements). A flip
    // re-fetches + re-lays-out the whole graph (the fetch effect keyed on
    // layoutDirection), moving every node far from its previous position (TB↔LR).
    // Because <ReactFlow> stays mounted, its one-shot `fitView` mount prop does not
    // re-run, and the height-measurement re-fit only fires when measured heights
    // actually change (they usually do not on a plain flip — same objects, other
    // axis) — so the re-positioned nodes fall outside the viewport and are left
    // off-screen. On a flip, request a re-fit: re-frame the deep-link focus node
    // when one is set (reset its one-shot guard so the focus effect re-runs), else
    // fit the whole re-laid-out graph once its nodes are committed (below). Skip the
    // initial mount — the mount `fitView` prop + the heights re-fit already frame it.
    const pendingFlipFitViewRef = useRef(false);
    const directionMountedRef = useRef(false);
    useEffect(() => {
        if (!directionMountedRef.current) {
            directionMountedRef.current = true;
            return;
        }
        if (focusId) {
            hasFocusedRef.current = false;
        } else {
            pendingFlipFitViewRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layoutDirection]);

    // Perform the deferred post-flip fitView once the re-laid-out nodes are in the
    // store. One requestAnimationFrame lets ReactFlow process the new positions
    // before we fit; a later heights-measurement re-fit (above) refines it.
    useEffect(() => {
        if (!pendingFlipFitViewRef.current || !reactFlowInstance || nodes.length === 0) {
            return;
        }
        pendingFlipFitViewRef.current = false;
        const raf = requestAnimationFrame(() => {
            reactFlowInstance.fitView({ maxZoom: 1.5, minZoom: 0.2, duration: 300 });
        });
        return () => cancelAnimationFrame(raf);
    }, [nodes, reactFlowInstance]);

    /**
     * Calculate and update children node heights
     * This effect runs after nodes are rendered to measure actual DOM heights
     */
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            // Calculate children height and pass it to parent
            nodes.forEach((node) => {
                if (node.data.children?.length > 0) {
                    const parentNodeHeight = node.data.children.reduce((height, childNode) => {
                        const selector = `[data-id="${childNode.id}"] .nowheel`;
                        const currentNode = document.querySelector(selector) as HTMLDivElement;

                        const currentHeight = currentNode?.offsetHeight || 66;
                        height += currentHeight;
                        return height;
                    }, 0);

                    if (parentNodeHeight > 0) {
                        setChildrenHeightData((prevHeightData) => {
                            if (prevHeightData[node.id] !== parentNodeHeight) {
                                return {
                                    ...prevHeightData,
                                    [node.id]: parentNodeHeight,
                                };
                            }
                            return prevHeightData;
                        });
                    }
                }
            });
        }, 100);

        return () => clearTimeout(timeoutId);
    }, [nodes]);

    // Reference to current nodes for use in effects
    const nodesRef = useRef<Node<any, string | undefined>[]>([]);

    useEffect(() => {
        nodesRef.current = nodes;
    }, [nodes]);

    /**
     * Handle expanded node height changes and reposition children
     * This effect recalculates positions when a node is expanded/collapsed
     */
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            // Calculate height of the expanded child node, get new children nodes position, update nodes
            const selector = `[data-id="${expandedCustomObjectId}"] .nowheel > div`;
            const currentExpandedNode = document.querySelector(selector) as HTMLDivElement;
            const expandedNodeHeight = currentExpandedNode?.offsetHeight;

            const nodesWithUpdatedHeight = nodesRef.current.map((currentNode) => {
                return {
                    ...currentNode,
                    height: currentNode.parentId
                        ? expandedCustomObjectId === currentNode.id
                            ? expandedNodeHeight
                            : CANVAS_CHILD_CARD_HEIGHT
                        : currentNode.height,
                };
            });

            // Separate parent and child nodes for positioning
            const positionedNodes = [] as any;
            const childrenNodeData = {} as {
                [parentId: string]: any[];
            };
            nodesWithUpdatedHeight.forEach((node) => {
                if (node.parentId) {
                    childrenNodeData[node.parentId] = [
                        ...(childrenNodeData[node.parentId] || []),
                        node,
                    ];
                } else {
                    positionedNodes.push(node);
                }
            });

            // Reposition children nodes and update state
            positionChildrenNodes(positionedNodes, childrenNodeData);
            setNodes(positionedNodes);
        }, 400);

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expandedCustomObjectId]);

    /**
     * Handle mouse enter events on edges to highlight them
     * Moves the hovered edge to the end of the edges array for proper z-index
     * @param edgeId - The ID of the edge being hovered
     */
    const handleMouseEnter = (edgeId) => {
        // On hover over edge change the order of edges, so the last edge in the dom is highlighted
        setEdges((prevEdges) => {
            const currentIndex = prevEdges.findIndex((item) => item.id === edgeId);

            if (currentIndex === -1 || currentIndex === prevEdges.length - 1) {
                return prevEdges;
            }
            const newEdges = [...prevEdges];
            const [movedEdge] = newEdges.splice(currentIndex, 1);
            newEdges.push(movedEdge);
            return newEdges;
        });
    };

    // Partial-view banner: shown for the deep-link (?focus) neighborhood while it is
    // still a bounded slice (unknown total, or total strictly greater than loaded).
    const showPartialBanner =
        !embedMode &&
        !!focusId &&
        isPartialView &&
        (totalCount === null || totalCount > loadedCount);

    return (
        <>
            <div
                className={`${styles.cardListContainer} ${
                    embedMode ? embedStyles.embeddedCanvasContainer : ""
                } ${className ?? ""}`}
            >
                {showPartialBanner && (
                    <div className={styles.partialViewBanner}>
                        {confirmingLoadAll ? (
                            <>
                                <span className={styles.partialViewText}>
                                    <Icon icon={IconCollection.informationOutline} />
                                    Load the full canvas? All templates load, with
                                    objects shown in pages per template. This can be slow
                                    on large workspaces.
                                </span>
                                <div className={styles.partialViewButtons}>
                                    <Button variation="primary"
                                        label="Load more"
                                        size="small"
                                        onClick={loadAllCanvas}
                                    />
                                    <Button
                                        label="Cancel"
                                        variation="white"
                                        size="small"
                                        onClick={() => setConfirmingLoadAll(false)}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <span className={styles.partialViewText}>
                                    <Icon icon={IconCollection.informationOutline} />
                                    Partial view — showing {loadedCount} of{" "}
                                    {totalCount === null ? (
                                        <CircularProgress
                                            disableShrink
                                            size={12}
                                            className={styles.partialViewSpinner}
                                        />
                                    ) : (
                                        totalCount
                                    )}
                                </span>
                                <div className={styles.partialViewButtons}>
                                    <Button variation="primary"
                                        label="Load more…"
                                        size="small"
                                        onClick={() => setConfirmingLoadAll(true)}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                )}
                {fetchError && (
                    <div className={styles.emptyData}>
                        <div>{fetchError}</div>
                    </div>
                )}
                {isLoading && (
                    <div className={styles.canvasLoader}>
                        <CircularProgress
                            disableShrink
                            className={`spinner ${isLoading ? "" : "hidden"}`}
                        />
                    </div>
                )}

                <div
                    className={`${styles.reactFlow} ${customObjectStyles.reactFlow}${
                        embedMode ? embedStyles.embeddedReactFlow : ""
                    } ${isLoading ? styles.reactFlowHidden : ""}`}
                >
                    {nodes.length > 0 ? (
                        <ReactFlow
                            nodes={displayNodes}
                            edges={edges.map((edge) => ({
                                ...edge,
                                data: {
                                    ...edge.data,
                                    onMouseEnter: () => handleMouseEnter(edge.id),
                                    // item 9: anchor edge handles on the axis that
                                    // matches the layout direction (LR -> left/right)
                                    layoutDirection,
                                },
                            }))}
                            nodeTypes={nodeTypes}
                            edgeTypes={edgeTypes}
                            fitView
                            fitViewOptions={{ maxZoom: 1.5, minZoom: 0.2 }}
                            minZoom={0.2}
                            onInit={(reactFlowInstance) => setReactFlowInstance(reactFlowInstance)}
                            onEdgesChange={onEdgesChange}
                        >
                            <Controls showInteractive={false}>
                                <Tooltip
                                    overlay={`Change to ${
                                        layoutDirection === Direction.Vertical
                                            ? "horizontal"
                                            : "vertical"
                                    } direction`}
                                >
                                    <ControlButton
                                        onClick={() => {
                                            setLayoutDirection((prevLayoutDirection) =>
                                                prevLayoutDirection === Direction.Vertical
                                                    ? Direction.Horizontal
                                                    : Direction.Vertical
                                            );
                                        }}
                                    >
                                        <Icon icon={IconCollection.rotateRight} />
                                    </ControlButton>
                                </Tooltip>
                            </Controls>
                        </ReactFlow>
                    ) : (
                        !isLoading &&
                        !fetchError && (
                            <div className={styles.emptyData}>
                                <div>No custom objects to display.</div>
                            </div>
                        )
                    )}
                </div>
            </div>
        </>
    );
};

/**
 * Wrapper component that provides ReactFlow context
 * @param props - Props to pass to CustomObjectCanvas
 * @returns JSX.Element - The wrapped CustomObjectCanvas component
 */
export const CustomObjectCanvasScreen = (props) => (
    <ReactFlowProvider>
        <CustomObjectCanvas {...props} />
    </ReactFlowProvider>
);
